import React, { useContext, useState } from "react";

import {
  Badge,
  Button,
  Container,
  Pagination,
  Table,
  Text,
} from "@mantine/core";

import { IconPlus } from "@tabler/icons-react";

import { AppName, Colors, SupportCategories } from "../../Lib/Data";

import DisplayAlert from "../../Components/DisplayAlert";
import MegaLoader from "../../Components/MegaLoader";
import { Endpoints } from "../../Lib/Endpoints";
import { getPaginationTotal } from "../../Lib/Methods";
import { PaginatedResponse } from "../../Lib/Responses";
import { PaginationProps, TicketProps } from "../../Lib/Types";
import { usePerformRequest } from "../../Lib/usePerformRequest";

import dayjs from "dayjs";
import { Link, useNavigate } from "react-router-dom";
import WhatsappChatBanner from "../../Components/WhatsAppChatBanner";
import "./styles.scss";
import { AppContext } from "../Wrapper";

const SupportPage: React.FC = () => {
  const navigate = useNavigate();
  const context = useContext(AppContext);
  const theme = context?.theme ?? "light";

  document.title = `Support | ${AppName}`;

  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 20,
  });
  const { data: data, isLoading: isLoadingTickets } = usePerformRequest<
    PaginatedResponse<TicketProps[]>
  >({
    method: "GET",
    route: Endpoints.GetUserTickets(pagination.page, pagination.limit),
  });

  const tableThProps: any = {
    // bg: theme === "dark" ? Colors.BlackPrimary : Colors.Transparent,
    c: theme === "dark" ? Colors.White : Colors.BlackPrimary,
  };

  return (
    <Container size="lg">
      <div className="support-container">
        <div className="flex-row align-center justify-between width-100">
          <Text
            fw={500}
            mb={30}
            c={theme === "dark" ? Colors.White : Colors.GreenSecondary}
          >
            Support History
          </Text>
          <Link to="/d/support/new">
            <Button
              leftSection={<IconPlus size={18} />}
              color={Colors.GreenPrimary}
              mb={30}
              disabled={!context || !context?.profile?.id}
            >
              New Request
            </Button>
          </Link>
        </div>

        <div className="items flex-col justify-between">
          {isLoadingTickets ? (
            <MegaLoader
              containerStyle={{
                marginTop: "20px",
                marginBottom: "20px",
              }}
            />
          ) : (
            <>
              {data && data.data.length > 0 ? (
                <div className="table-container">
                  <Table verticalSpacing="md">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th {...tableThProps}>Created</Table.Th>
                        <Table.Th {...tableThProps}>Title</Table.Th>
                        <Table.Th {...tableThProps}>Category</Table.Th>
                        <Table.Th {...tableThProps}>Status</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {data.data.map((ticket: TicketProps) => {
                        return (
                          <Table.Tr
                            key={ticket.id}
                            onClick={() => {
                              navigate(`/d/support/view/${ticket.id}`);
                            }}
                            {...tableThProps}
                          >
                            <Table.Td>
                              {dayjs(ticket.createdAt).format("DD MMM YYYY")}
                            </Table.Td>
                            <Table.Td>{ticket.title}</Table.Td>
                            <Table.Td>
                              {
                                SupportCategories.find(
                                  (c) => c.value === ticket.category
                                )?.label
                              }
                            </Table.Td>
                            <Table.Td>
                              <Badge
                                size="sm"
                                color={
                                  ticket.status === "resolved"
                                    ? Colors.GreenPrimary
                                    : Colors.OrangeSecondary
                                }
                              >
                                {ticket.status}
                              </Badge>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </div>
              ) : (
                <>
                  {context && context.profile && context.profile.id ? (
                    <DisplayAlert
                      containerStyle={{
                        marginTop: "20px",
                      }}
                      title={"No History"}
                      children={
                        <div className="flex-col">
                          <Text fz="sm">You have no support history</Text>
                        </div>
                      }
                    />
                  ) : (
                    <DisplayAlert
                      containerStyle={{
                        marginTop: "20px",
                      }}
                      title="You are not logged in"
                      children={
                        <div className="flex-col">
                          <Text fz="sm">
                            You must log in to access support!
                          </Text>
                        </div>
                      }
                    />
                  )}
                </>
              )}
              <div className="flex-row align-center justify-center width-100">
                <Pagination
                  value={pagination.page}
                  onChange={(value) => {
                    setPagination({ ...pagination, page: value });
                  }}
                  my={20}
                  disabled={isLoadingTickets}
                  total={getPaginationTotal(
                    data?.totalRecords,
                    pagination.limit
                  )}
                />
              </div>
            </>
          )}
          <WhatsappChatBanner />
        </div>
      </div>
    </Container>
  );
};

export default SupportPage;
